import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import { useDispatch } from "react-redux";
import { loginSuccess } from "../reducers/authSlice";
import api from "../api/axiosInstance";

const imageSrc = `${process.env.PUBLIC_URL}/g3.png`;

interface RegisterModalProps {
    isOpen: boolean;
    onRequestClose: () => void;
    onRegister: () => void;
}

// Modal background and gradient styles to match the mobile look
const GradientBackground = styled.div`
    background: linear-gradient(135deg, #f56607, #000);
    padding: 30px 20px 25px;
    border-radius: 16px;
    width: 100%;
    max-width: 320px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    box-shadow: 0px 15px 35px rgba(0, 0, 0, 0.9);
    position: relative;
    border: 1px solid rgba(255, 255, 255, 0.1);

    @media (min-width: 375px) {
        padding: 35px 25px 30px;
        max-width: 350px;
    }

    @media (min-width: 414px) {
        padding: 40px 30px 35px;
        max-width: 380px;
    }

    @media (min-width: 768px) {
        padding: 50px 40px 40px;
        max-width: 480px;
        border-radius: 20px;
    }
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 16px;

    @media (min-width: 768px) {
        gap: 20px;
    }
`;

const Label = styled.label`
    font-weight: 600;
    color: white;
    text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.5);
    font-size: 14px;
    margin-bottom: 4px;
    display: block;

    @media (min-width: 375px) {
        font-size: 15px;
    }
`;

const Input = styled.input`
    padding: 14px;
    width: 100%;
    border-radius: 10px;
    border: 2px solid rgba(255, 255, 255, 0.2);
    background-color: rgba(255, 255, 255, 0.12);
    color: white;
    font-size: 15px;
    transition: all 0.3s ease;
    box-sizing: border-box;
    min-height: 48px;

    &::placeholder {
        color: #ccc;
        font-weight: 400;
        font-size: 14px;
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(245, 102, 7, 0.3);
        border-color: rgba(245, 102, 7, 0.6);
        background-color: rgba(255, 255, 255, 0.15);
    }

    &:hover {
        border-color: rgba(255, 255, 255, 0.3);
    }

    @media (min-width: 375px) {
        padding: 15px;
        font-size: 16px;
        min-height: 50px;
    }

    @media (min-width: 768px) {
        padding: 16px;
        border-radius: 12px;
        min-height: 52px;
    }
`;

const Button = styled.button<{ disabled?: boolean }>`
    padding: 16px;
    background: ${props => props.disabled
            ? 'rgba(255, 255, 255, 0.1)'
            : 'linear-gradient(135deg, #f56607 0%, #ff8c00 100%)'};
    color: white;
    border: none;
    border-radius: 10px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    font-size: 16px;
    font-weight: 700;
    text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.5);
    box-shadow: ${props => props.disabled
            ? '0 4px 12px rgba(255, 255, 255, 0.1)'
            : '0 6px 18px rgba(245, 102, 7, 0.4)'};
    transition: all 0.3s ease;
    margin-top: 8px;
    letter-spacing: 0.3px;
    min-height: 52px;
    width: 100%;

    &:hover {
        background: ${props => props.disabled
                ? 'rgba(255, 255, 255, 0.1)'
                : 'linear-gradient(135deg, #ff8c00 0%, #f56607 100%)'};
        transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
        box-shadow: ${props => props.disabled
                ? '0 4px 12px rgba(255, 255, 255, 0.1)'
                : '0 8px 20px rgba(245, 102, 7, 0.5)'};
    }

    &:active {
        transform: ${props => props.disabled ? 'none' : 'translateY(-1px)'};
    }

    @media (min-width: 768px) {
        padding: 18px;
        border-radius: 12px;
        font-size: 17px;
        min-height: 56px;
    }
`;

const Header = styled.h2`
    text-align: center;
    color: white;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.6);
    margin-bottom: 20px;
    font-size: 24px;
    font-weight: 800;
    background: linear-gradient(135deg, #ffffff 0%, #ffa726 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1.2;

    @media (min-width: 375px) {
        font-size: 26px;
        margin-bottom: 25px;
    }

    @media (min-width: 414px) {
        font-size: 28px;
    }

    @media (min-width: 768px) {
        font-size: 32px;
        margin-bottom: 30px;
    }
`;

const ErrorMessage = styled.p`
    color: #ff6b6b;
    text-align: center;
    margin: 0;
    background: rgba(255, 107, 107, 0.15);
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid rgba(255, 107, 107, 0.4);
    font-weight: 500;
    font-size: 13px;
    line-height: 1.4;

    @media (min-width: 375px) {
        font-size: 14px;
        padding: 12px 14px;
    }

    @media (min-width: 768px) {
        padding: 12px 16px;
        border-radius: 10px;
        font-size: 14px;
    }
`;

const CheckboxContainer = styled.div`
    display: flex;
    align-items: flex-start;
    margin: 16px 0 8px;
    gap: 10px;
    padding: 12px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.1);

    @media (min-width: 375px) {
        padding: 14px;
        gap: 12px;
    }

    @media (min-width: 768px) {
        padding: 15px;
        border-radius: 12px;
        margin: 20px 0 10px;
    }
`;

const Checkbox = styled.input`
    margin-top: 2px;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    cursor: pointer;

    &:checked {
        accent-color: #f56607;
    }

    @media (min-width: 375px) {
        width: 17px;
        height: 17px;
    }

    @media (min-width: 768px) {
        width: 18px;
        height: 18px;
        margin-top: 3px;
    }
`;

const TermsText = styled.span`
    color: #f0f0f0;
    font-size: 12px;
    line-height: 1.4;
    font-weight: 400;

    @media (min-width: 375px) {
        font-size: 13px;
        line-height: 1.5;
    }

    @media (min-width: 768px) {
        font-size: 14px;
    }
`;

const TermsLink = styled.a`
    color: #ffa726;
    text-decoration: underline;
    font-weight: 600;
    transition: all 0.3s ease;
    font-size: inherit;

    &:hover {
        color: #ffcc80;
        text-decoration: none;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    color: white;
    font-size: 20px;
    cursor: pointer;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.3s ease;
    font-weight: bold;

    &:hover {
        background: rgba(255, 255, 255, 0.2);
        color: #ffa726;
        transform: scale(1.1);
    }

    &:active {
        transform: scale(1);
    }

    @media (min-width: 375px) {
        top: 15px;
        right: 15px;
        width: 34px;
        height: 34px;
        font-size: 22px;
    }

    @media (min-width: 768px) {
        top: 20px;
        right: 20px;
        width: 40px;
        height: 40px;
        font-size: 24px;
    }
`;

const InputGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;

    @media (min-width: 768px) {
        gap: 8px;
    }
`;

// Perfectly centered modal styles for all devices
const customStyles = {
    content: {
        width: 'calc(100% - 40px)',
        maxWidth: '320px',
        height: 'auto',
        maxHeight: '85vh',
        margin: '0',
        padding: '0',
        borderRadius: '16px',
        border: 'none',
        background: 'none',
        position: 'fixed' as const,
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
        overflow: 'auto',
        boxShadow: 'none',
        WebkitOverflowScrolling: 'touch' as const,
    },
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        backdropFilter: 'blur(6px)',
        zIndex: 10000,
        position: 'fixed' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px',
        WebkitTapHighlightColor: 'transparent',
    }
};

// Media queries for different screen sizes
const updateModalStyles = (width: number) => {
    if (width >= 768) {
        return {
            ...customStyles,
            content: {
                ...customStyles.content,
                maxWidth: '480px',
                borderRadius: '20px',
                maxHeight: '90vh',
            }
        };
    } else if (width >= 414) {
        return {
            ...customStyles,
            content: {
                ...customStyles.content,
                maxWidth: '380px',
            }
        };
    } else if (width >= 375) {
        return {
            ...customStyles,
            content: {
                ...customStyles.content,
                maxWidth: '350px',
            }
        };
    }
    return customStyles;
};

const RegisterModal: React.FC<RegisterModalProps> = ({ isOpen, onRequestClose, onRegister }) => {
    const [email, setEmail] = useState('');
    const [fullName, setFullName] = useState('');
    const [password, setPassword] = useState('');
    const [password2, setPassword2] = useState('');
    const [role] = useState('USER');
    const [error, setError] = useState<string | null>(null);
    const [isTermsChecked, setIsTermsChecked] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [windowWidth, setWindowWidth] = useState(320);

    const dispatch = useDispatch();

    useEffect(() => {
        setIsMounted(true);
        setWindowWidth(window.innerWidth);

        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            setIsMounted(false);
        };
    }, []);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!email || !fullName || !password || !password2) {
            setError("Všetky polia sú povinné.");
            return;
        }

        if (password !== password2) {
            setError("Heslá sa nezhodujú");
            return;
        }

        if (password.length < 6) {
            setError("Heslo musí mať aspoň 6 znakov");
            return;
        }

        if (!isTermsChecked) {
            setError("Musíte súhlasiť s podmienkami používania.");
            return;
        }

        try {
            setError(null);
            const referralSource = localStorage.getItem('referral_source');
            const response = await api.post('/v1/auth/register', {
                email,
                password,
                role,
                fullName,
                referralSource
            });
            const { accessToken, refreshToken, user } = response.data;
            dispatch(loginSuccess({ accessToken, refreshToken, user }));

            // Reset form
            setEmail('');
            setFullName('');
            setPassword('');
            setPassword2('');
            setError(null);
            setIsTermsChecked(false);

            onRequestClose();
            onRegister();
        } catch (error: any) {
            console.error('Registration error:', error);
            if (error.response && error.response.status === 409) {
                setError("Používateľ s týmto emailom už existuje.");
            } else if (error.response && error.response.data?.message) {
                setError(error.response.data.message);
            } else {
                setError("Registrácia zlyhala. Skúste to znova.");
            }
        }
    };

    const handleClose = () => {
        setEmail('');
        setFullName('');
        setPassword('');
        setPassword2('');
        setError(null);
        setIsTermsChecked(false);
        onRequestClose();
    };

    if (!isMounted) {
        return null;
    }

    const currentModalStyles = updateModalStyles(windowWidth);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleClose}
            style={currentModalStyles}
            ariaHideApp={false}
            closeTimeoutMS={300}
            shouldCloseOnOverlayClick={true}
            shouldCloseOnEsc={true}
        >
            <GradientBackground>
                <CloseButton
                    onClick={handleClose}
                    aria-label="Zatvoriť registráciu"
                    type="button"
                >
                    ×
                </CloseButton>

                <Header>Registrácia</Header>

                <Form onSubmit={handleSubmit}>
                    {error && <ErrorMessage>{error}</ErrorMessage>}

                    <InputGroup>
                        <Label htmlFor="email">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            placeholder="vas@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </InputGroup>

                    <InputGroup>
                        <Label htmlFor="fullName">Celé meno</Label>
                        <Input
                            id="fullName"
                            type="text"
                            placeholder="Janko Mrkvička"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            required
                        />
                    </InputGroup>

                    <InputGroup>
                        <Label htmlFor="password">Heslo</Label>
                        <Input
                            id="password"
                            type="password"
                            placeholder="Zadajte heslo"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                        />
                    </InputGroup>

                    <InputGroup>
                        <Label htmlFor="password2">Zopakuj heslo</Label>
                        <Input
                            id="password2"
                            type="password"
                            placeholder="Zopakujte heslo"
                            value={password2}
                            onChange={(e) => setPassword2(e.target.value)}
                            required
                            minLength={6}
                        />
                    </InputGroup>

                    <CheckboxContainer>
                        <Checkbox
                            type="checkbox"
                            checked={isTermsChecked}
                            onChange={(e) => setIsTermsChecked(e.target.checked)}
                            id="terms-checkbox"
                        />
                        <TermsText>
                            Mám viac ako 18 rokov a súhlasím s{" "}
                            <TermsLink
                                href="/podmienky"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                            >
                                obchodnými podmienkami a podmienkami používania.
                            </TermsLink>
                        </TermsText>
                    </CheckboxContainer>

                    <Button
                        type="submit"
                        disabled={!isTermsChecked}
                    >
                        Vytvoriť účet
                    </Button>
                </Form>
            </GradientBackground>
        </Modal>
    );
};

export default RegisterModal;